import React, { useState, useMemo } from 'react';
import {
    Paper,
    Typography,
    Button,
    Menu,
    MenuItem,
    Checkbox,
    ListItemIcon,
    ListItemText,
    Divider,
    Box,
    CircularProgress,
    ListSubheader,
    Tooltip,
    TextField
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import i18n from '../../helpers/i18n';

const initialSettings = {
    'ahgora_multi_device': {
        only_with_location: false,
        enable_password_punch: false,
        triple_validation: false,
        enable_punch_by_manager: false,
        online_only: false,
        auto_timezone: false,
    },
    'my_ahgora_device': {
        only_with_location: false
    }
};

const deviceSpecificSettingsMap = {
    'Multi': [
        'enable_password_punch', 'triple_validation',
        'enable_punch_by_manager', 'online_only',
        'auto_timezone', 'only_with_location'
    ],
    'My Ahgora': [
        'only_with_location'
    ],
    // Adicione outros tipos de dispositivo e suas configurações permitidas aqui
    // 'Batida Online': ['online_only', 'auto_timezone'],
    // 'Biometria': ['only_with_location', 'enable_password_punch'],
};

export default function BulkActionsFooter({ selectedDevicesData, onApplyActions, isLoading, permissions }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);

    const [archiveAction, setArchiveAction] = useState(false);
    const [deactivateAction, setDeactivateAction] = useState(false);
    const [currentSettings, setCurrentSettings] = useState(initialSettings);

    const uniqueDeviceTypes = useMemo(() => {
        if (!selectedDevicesData || selectedDevicesData.length === 0) return [];
        const types = selectedDevicesData.map(d => d.deviceType);
        
        return [...new Set(types.filter(type => !!type))];
    }, [selectedDevicesData]);

    const handleClickMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleSettingChange = (deviceTypeKey, settingId) => {
        setCurrentSettings(prev => ({
            ...prev,
            [deviceTypeKey]: {
                ...prev[deviceTypeKey],
                [settingId]: !prev[deviceTypeKey][settingId]
            }
        }));
    };

    const formatDeviceConfigKey = (deviceType) => {
        const typeMapping = {
            'Multi': 'ahgora_multi_device',
            'My Ahgora': 'my_ahgora_device'
        };
        return typeMapping[deviceType];
    };

    const handleApply = () => {
        const deviceConfigs = uniqueDeviceTypes.reduce((acc, deviceType) => {
            const deviceTypeKey = formatDeviceConfigKey(deviceType);
            if (!deviceTypeKey) return acc;

            const allowedSettings = deviceSpecificSettingsMap[deviceType] || [];
            const settings = allowedSettings.reduce((settingsAcc, settingId) => {
                settingsAcc[settingId] = currentSettings[deviceTypeKey][settingId];
                return settingsAcc;
            }, {});
            
            acc[deviceTypeKey] = settings;
            return acc;
        }, {});

        const globalActions = { archive: archiveAction };
        if (deactivateAction) {
            globalActions.deactivate = true;
        }

        const payload = {
            devices: selectedDevicesData.map(device => device.id),
            global_actions: globalActions,
            ...(!archiveAction && !deactivateAction && { device_configs: deviceConfigs })
        };

        onApplyActions(payload);
        handleCloseMenu();
    };

    const selectedDeviceNames = selectedDevicesData.map(d => d.name).join(', ');
    const displayDeviceNames = selectedDeviceNames.length > 100
        ? `${selectedDeviceNames.substring(0, 97)}...`
        : selectedDeviceNames;

    const isAnyActionSelected = archiveAction || deactivateAction || 
        Object.values(currentSettings).some(deviceSettings => 
            Object.values(deviceSettings).some(setting => setting)
        );
    const isAnyGlobalActionSelected = archiveAction || deactivateAction;

    if (!selectedDevicesData || selectedDevicesData.length <= 1) {
        return null;
    }

    return (
        <Paper
            elevation={3}
            sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#333333',
                color: 'white',
                borderRadius: '0px',
                zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
        >
            <Box sx={{ flexGrow: 1, mr: 2, display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle1" component="span" sx={{ mr: 1 }}>
                    {i18n.t('bulkActions.title')}{' '}
                </Typography>
                <Tooltip title={selectedDeviceNames}>
                    <TextField
                        variant="outlined"
                        size="small"
                        value={displayDeviceNames}
                        disabled
                        sx={{
                            '& .MuiInputBase-root': {
                                backgroundColor: 'white',
                                borderRadius: '10px',
                                color: 'var(--ahg-waterloo-darken1)',
                                fontWeight: 'regular',
                                fontSize: '14px',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'transparent',
                            },
                            '& .Mui-disabled': {
                                '-webkit-text-fill-color': 'black',
                                color: 'black',
                                cursor: 'default'
                            },
                            flexGrow: 1,
                            width: '70%'
                        }}
                        InputProps={{
                            readOnly: true,
                        }}
                    />
                </Tooltip>
            </Box>

            <Button
                id="bulk-actions-button"
                aria-controls={openMenu ? 'bulk-actions-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={openMenu ? 'true' : undefined}
                variant="contained"
                onClick={handleClickMenu}
                endIcon={<ArrowDropDownIcon />}
                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
                disabled={isLoading}
                color='primary'
                sx={{
                    color: 'var(--ahg-primary-dark)',
                    textTransform: 'none',
                }}
            >
                {i18n.t('bulkActions.triggerButton')}
            </Button>
            <Menu
                id="bulk-actions-menu"
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleCloseMenu}
                MenuListProps={{
                    'aria-labelledby': 'bulk-actions-button',
                }}
            >
                {/* Ações Globais */}
                <MenuItem 
                    onClick={() => setArchiveAction(prev => !prev)}
                >
                    <ListItemIcon>
                        <Checkbox 
                            checked={archiveAction} 
                            size="small" 
                        />
                    </ListItemIcon>
                    <ListItemText primary={i18n.t('bulkActions.archiveLabel')} />
                </MenuItem>

                <MenuItem 
                    onClick={() => setDeactivateAction(prev => !prev)}
                >
                    <ListItemIcon>
                        <Checkbox 
                            checked={deactivateAction} 
                            size="small" 
                        />
                    </ListItemIcon>
                    <ListItemText primary={i18n.t('bulkActions.deactivateLabel')} />
                </MenuItem>

                {/* Seção Multi */}
                {uniqueDeviceTypes.includes('Multi') && (
                    <>
                        <Divider />
                        <ListSubheader>
                            {i18n.t('bulkActions.multiSettingsHeader')}
                        </ListSubheader>

                        {/* Ativar batida por matrícula e senha */}
                        <MenuItem 
                            onClick={() => handleSettingChange('ahgora_multi_device', 'enable_password_punch')}
                            disabled={isAnyGlobalActionSelected}
                        >
                            <ListItemIcon>
                                <Checkbox 
                                    checked={currentSettings.ahgora_multi_device.enable_password_punch} 
                                    size="small" 
                                    disabled={isAnyGlobalActionSelected}
                                />
                            </ListItemIcon>
                            <ListItemText primary={i18n.t('pages.newDevice.punchRegistrationPassword')} />
                        </MenuItem>

                        {/* Ativar validação tripla para múltiplos usuários */}
                        <MenuItem 
                            onClick={() => handleSettingChange('ahgora_multi_device', 'triple_validation')}
                            disabled={isAnyGlobalActionSelected}
                        >
                            <ListItemIcon>
                                <Checkbox 
                                    checked={currentSettings.ahgora_multi_device.triple_validation} 
                                    size="small" 
                                    disabled={isAnyGlobalActionSelected}
                                />
                            </ListItemIcon>
                            <ListItemText primary={i18n.t('pages.newDevice.tripleValidation')} />
                        </MenuItem>

                        {/* Habilita batida por gestor */}
                        { permissions?.enable_punch_by_manager &&
                            <MenuItem 
                                onClick={() => handleSettingChange('ahgora_multi_device', 'enable_punch_by_manager')}
                                disabled={isAnyGlobalActionSelected}
                            >
                                <ListItemIcon>
                                    <Checkbox 
                                        checked={currentSettings.ahgora_multi_device.enable_punch_by_manager} 
                                        size="small" 
                                        disabled={isAnyGlobalActionSelected}
                                    />
                                </ListItemIcon>
                                <ListItemText primary={i18n.t('pages.newDevice.managerPunch')} />
                            </MenuItem>
                        }

                        {permissions?.permite_restricao_batida && (
                            <>
                                {/* Com o dispositivo conectado à internet */}
                                <MenuItem 
                                    onClick={() => handleSettingChange('ahgora_multi_device', 'online_only')}
                                    disabled={isAnyGlobalActionSelected}
                                >
                                    <ListItemIcon>
                                        <Checkbox 
                                            checked={currentSettings.ahgora_multi_device.online_only} 
                                            size="small" 
                                            disabled={isAnyGlobalActionSelected}
                                        />
                                    </ListItemIcon>
                                    <ListItemText primary={i18n.t('pages.newDevice.networkConnection')} />
                                </MenuItem>

                                {/* Com data, hora e fuso automáticos */}
                                <MenuItem 
                                    onClick={() => handleSettingChange('ahgora_multi_device', 'auto_timezone')}
                                    disabled={isAnyGlobalActionSelected}
                                >
                                    <ListItemIcon>
                                        <Checkbox 
                                            checked={currentSettings.ahgora_multi_device.auto_timezone} 
                                            size="small" 
                                            disabled={isAnyGlobalActionSelected}
                                        />
                                    </ListItemIcon>
                                    <ListItemText primary={i18n.t('pages.newDevice.dateHourTimezoneAuto')} />
                                </MenuItem>

                                {/* Com geolocalização no dispositivo */}
                                <MenuItem 
                                    onClick={() => handleSettingChange('ahgora_multi_device', 'only_with_location')}
                                    disabled={isAnyGlobalActionSelected}
                                >
                                    <ListItemIcon>
                                        <Checkbox 
                                            checked={currentSettings.ahgora_multi_device.only_with_location} 
                                            size="small" 
                                            disabled={isAnyGlobalActionSelected}
                                        />
                                    </ListItemIcon>
                                    <ListItemText primary={i18n.t('pages.newDevice.geolocation')} />
                                </MenuItem>
                            </>
                        )}
                    </>
                )}

                {/* Seção My Ahgora */}
                {permissions?.permite_restricao_batida && uniqueDeviceTypes.includes('My Ahgora') && (
                    <>
                        <Divider />
                        <ListSubheader>
                            {i18n.t('bulkActions.myAhgoraSettingsHeader')}
                        </ListSubheader>

                        {/* Com geolocalização no dispositivo */}
                        <MenuItem 
                            onClick={() => handleSettingChange('my_ahgora_device', 'only_with_location')}
                            disabled={isAnyGlobalActionSelected}
                        >
                            <ListItemIcon>
                                <Checkbox 
                                    checked={currentSettings.my_ahgora_device.only_with_location} 
                                    size="small" 
                                    disabled={isAnyGlobalActionSelected}
                                />
                            </ListItemIcon>
                            <ListItemText primary={i18n.t('pages.newDevice.geolocation')} />
                        </MenuItem>
                    </>
                )}

                {/* Botão de Aplicar */}
                <MenuItem>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleApply}
                        disabled={isLoading || !isAnyActionSelected}
                        sx={{ width: '100%' }}
                    >
                        {isLoading ? <CircularProgress size={24} /> : i18n.t('bulkActions.applyButton')}
                    </Button>
                </MenuItem>
            </Menu>
        </Paper>
    );
}
